import EntityAudits from "@/components/EntityAudits.vue";
import {
  EntityDocumentForm,
  EntityDocumentMultipleForm,
  EntityDocuments,
  EntityDocumentsChildList,
} from "@modernary/mui/components/document";
import { EntityNotes } from "@modernary/mui/components/note";
import { RouterView, type RouterOptions } from "vue-router";
import type ComplianceEventChildList from "./ComplianceEventChildList.vue";
import type NotificationFormChildList from "./NotificationFormChildList.vue";

const names = {
  facilities: "Facilities.Facilities",
  list: "Facilities.FacilityList",
  new: "Facilities.FacilityNew",
  base: "Facilities.FacilityBase",
  view: "Facilities.FacilityView,",
  notes: "Facilities.FacilityNotes",
  noteEdit: "Facilities.FacilityNoteEdit",
  noteNew: "Facilities.FacilityNoteNew",
  edit: "Facilities.FacilityEdit",
  tankChildList: "Facilities.FacilityTankChildList",
  releaseChildList: "Facilities.FacilityReleaseChildList",
  complianceEventChildList: "Facilities.FacilityComplianceEventChildList",
  notificationFormChildList: "Facilities.FacilityNotificationFormChildList",
  notificationFormNew: "Facilities.FacilityNotificationFormNew",
  notificationFormEdit: "Facilities.FacilityNotificationFormEdit",
  financialAssuranceChildList: "Facilities.FacilityFinancialAssuranceChildList",
  financialAssuranceNew: "Facilities.FacilityFinancialAssuranceNew",
  financialAssuranceEdit: "Facilities.FacilityFinancialAssuranceEdit",
  dispenserChildList: "Facilities.FacilityDispenserChildList",
  dispenserNew: "Facilities.FacilityDispenserNew",
  dispenserEdit: "Facilities.FacilityDispenserEdit",
  permitChildList: "Facility.PermitChildList",
  documents: "Facilities.FacilityDocuments",
  documentEdit: "Facilities.FacilityDocumentEdit",
  documentNew: "Facilities.FacilityDocumentNew",
  audit: "Facilities.FacilityAudit",
};

export { names as facilityRouteNames };

const facilityRoutes: RouterOptions["routes"] = [
  {
    path: "/facilities",
    name: names.facilities,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.list },
    children: [
      {
        path: "list",
        name: names.list,
        meta: {
          title: "Facilities",
        },
        component: () => import("@/views/facility/FacilityList.vue"),
      },
      {
        path: "new",
        component: () => import("@/views/facility/FacilityBase.vue"),
        redirect: { name: names.new },
        props: true,
        children: [
          {
            path: "",
            name: names.new,
            meta: { title: "Facility New", isNew: true },
            component: () => import("@/views/facility/FacilityEdit.vue"),
          },
        ],
      },
      {
        path: ":id(\\d+)",
        name: names.base,
        component: () => import("@/views/facility/FacilityBase.vue"),
        redirect: { name: names.view },
        children: [
          {
            path: "view",
            name: names.view,
            meta: { title: "Facility View" },
            component: () => import("@/views/facility/FacilityView.vue"),
          },
          {
            path: "edit",
            name: names.edit,
            meta: { title: "Facility Edit", isForm: true },
            component: () => import("@/views/facility/FacilityEdit.vue"),
          },
          {
            path: "tanks",
            name: names.tankChildList,
            meta: { title: "Facility Tanks" },
            component: () => import("@/views/facility/TankChildList.vue"),
          },
          {
            path: "releases",
            name: names.releaseChildList,
            meta: { title: "Facility Releases" },
            component: () => import("@/views/facility/ReleaseChildList.vue"),
          },
          {
            path: "complianceEvents",
            name: names.complianceEventChildList,
            meta: { title: "Facility Compliance Events" },
            component: () => import("@/views/facility/ComplianceEventChildList.vue"),
          },
          {
            path: "notificationForms",
            name: names.notificationFormChildList,
            meta: { title: "Facility Notification Forms" },
            component: () => import("@/views/facility/NotificationFormChildList.vue"),
          },
          {
            path: "notificationForms/new",
            name: names.notificationFormNew,
            meta: { title: "Facility Notification Form New", isForm: true },
            component: () => import("@/views/facility/NotificationFormEdit.vue"),
          },
          {
            path: "notificationForms/:notificationFormId(\\d+)",
            name: names.notificationFormEdit,
            meta: { title: "Facility Notification Form Edit", isForm: true },
            component: () => import("@/views/facility/NotificationFormEdit.vue"),
          },
          {
            path: "financialAssurances",
            name: names.financialAssuranceChildList,
            meta: { title: "Facility Financial Assurance" },
            component: () => import("@/views/facility/FinancialAssuranceChildList.vue"),
          },
          {
            path: "financialAssurances/new",
            name: names.financialAssuranceNew,
            meta: { title: "Facility Financial Assurance New", isForm: true },
            component: () => import("@/views/facility/FinancialAssuranceEdit.vue"),
          },
          {
            path: "financialAssurances/:financialAssuranceId(\\d+)",
            name: names.financialAssuranceEdit,
            meta: { title: "Facility Financial Assurance Edit", isForm: true },
            component: () => import("@/views/facility/FinancialAssuranceEdit.vue"),
          },
          {
            path: "dispensers",
            name: names.dispenserChildList,
            meta: { title: "Facility Dispensers" },
            component: () => import("@/views/facility/DispenserChildList.vue"),
          },
          {
            path: "dispenser/new",
            name: names.dispenserNew,
            meta: { title: "Facility Dispenser New", isForm: true },
            component: () => import("@/views/facility/DispenserEdit.vue"),
          },
          {
            path: "dispenser/:dispenserId(\\d+)",
            name: names.dispenserEdit,
            meta: { title: "Facility Dispenser Edit", isForm: true },
            component: () => import("@/views/facility/DispenserEdit.vue"),
          },
          {
            path: "permits",
            name: names.permitChildList,
            meta: { title: "Facility Permits" },
            component: () => import("@/views/facility/PermitChildList.vue"),
          },
          {
            path: "notes",
            name: names.notes,
            meta: { title: "Facility Notes" },
            component: {
              render: () => <EntityNotes idField="facilityId" routeNamePrefix="Facilities.FacilityNote" />,
            },
          },
          {
            path: "notes/:annotationId(\\d+)",
            name: names.noteEdit,
            meta: { title: "Facility Note Edit", isForm: true },
            component: () => import("@/views/facility/FacilityNote.vue"),
          },
          {
            path: "notes/new",
            name: names.noteNew,
            meta: { title: "Facility Note New", isForm: true },
            component: () => import("@/views/facility/FacilityNote.vue"),
          },
          {
            path: "documents",
            name: names.documents,
            meta: { title: "Facility Documents" },
            component: {
              render: () => <EntityDocumentsChildList resourceTypeKey="facility" />,
            },
          },
          {
            path: "documents/:documentId(\\d+)",
            name: names.documentEdit,
            meta: { title: "Facility Document Edit", isForm: true },
            component: {
              render: () => <EntityDocumentForm resourceTypeKey="facility" />,
            },
          },
          {
            path: "documents/new",
            name: names.documentNew,
            meta: { title: "Facility Document New", isForm: true },
            component: {
              render: () => <EntityDocumentMultipleForm resourceTypeKey="facility" />,
            },
          },
          {
            path: "activity",
            name: names.audit,
            meta: { title: "Facility Audit Activity" },
            component: {
              render: () => <EntityAudits entityName="Facility" />,
            },
          },
        ],
      },
    ],
  },
];
export default facilityRoutes;
